import * as Sentry from '@sentry/react';
import { create } from 'zustand';

import type { AuthUser } from '@wellstone-solutions/api/v2';

export type AuthState = {
  // Registration states
  isRegistering: boolean;
  isPendingVerification: boolean;
  isEmailVerified: boolean;

  // Verification error state
  verificationError: string | null;

  // Login states
  isReadyToLogin: boolean;
  isLoggedIn: boolean;

  // Tokens
  accessToken: string | null;
  refreshToken: string | null;

  // Full Authenticated fetched user (post-login)
  authUser: AuthUser | null;

  // Partial info used during registration
  userInfo: {
    email?: string;
    screenName?: string;
  } | null;

  // Status flags
  isPendingUserInfo: boolean;
  isPendingScreenName: boolean;

  // Actions
  startRegistration: () => void;
  setRegistrationSuccess: () => void;
  setEmailVerified: () => void;
  resendVerificationEmail: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  updateAccessToken: (accessToken: string) => void;
  setReadyToLogin: () => void;
  setUserLoggedIn: () => void;
  setUserInfo: (info: AuthState['userInfo']) => void;
  setAuthUser: (user: AuthUser) => void;
  setScreenName: (screenName: string) => void;
  setVerificationError: (message: string) => void;
  clearVerificationError: () => void;
  reset: () => void;
};

const initialState = {
  isRegistering: false,
  isPendingVerification: false,
  isEmailVerified: false,
  verificationError: null,
  isReadyToLogin: false,
  isLoggedIn: false,
  accessToken: null,
  refreshToken: null,
  authUser: null,
  userInfo: null,
  isPendingUserInfo: false,
  isPendingScreenName: false,
  isPendingPasswordReset: false,
} as const;

export const useAuthStore = create<AuthState>()((set) => ({
  ...initialState,

  startRegistration: () => set({ isRegistering: true }),

  setRegistrationSuccess: () =>
    set({
      isRegistering: false,
      isPendingVerification: true,
      verificationError: null, // Clear any previous errors
    }),

  setEmailVerified: () =>
    set({
      isPendingVerification: false,
      isEmailVerified: true,
      isReadyToLogin: true,
      verificationError: null, // Clear any previous errors
    }),

  resendVerificationEmail: () =>
    set({
      isPendingVerification: true,
    }),

  setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),

  updateAccessToken: (accessToken) => set({ accessToken }),

  setReadyToLogin: () => set({ isReadyToLogin: true }),

  setUserLoggedIn: () =>
    set({
      isLoggedIn: true,
      isPendingUserInfo: true,
    }),

  setUserInfo: (info) =>
    set({
      userInfo: info,
      isPendingUserInfo: false,
      isPendingScreenName: true,
    }),

  setAuthUser: (authUser) => {
    set({
      authUser,
      isPendingUserInfo: false,
      isPendingScreenName: !authUser?.name,
    });

    Sentry.setUser({ id: authUser.id });
  },

  setScreenName: (screenName) =>
    set((state) => ({
      userInfo: {
        ...state.userInfo,
        screenName,
      },
      authUser: state.authUser ? { ...state.authUser, name: screenName } : null,
      isPendingScreenName: false,
    })),

  setVerificationError: (message: string) =>
    set({
      verificationError: message,
      isPendingVerification: false,
    }),

  clearVerificationError: () =>
    set({
      verificationError: null,
    }),

  reset: () => {
    set(initialState);
    Sentry.setUser(null);
  },
}));
